/* ==========================================================================
   footballlab — fbl.js
   Vanilla JS, ziadne kniznice. Nacitane s `defer`.

   Vsetko su custom elementy — prezijú re-render sekcie v Theme Editore
   (`shopify:section:load`) bez toho, aby sme na tu udalost museli pocuvat:
   prehliadac zavola connectedCallback znova sam.

   Stranka musi fungovat aj bez JS. Tento subor ju iba vylepsuje:
   - hlavicka je bez JS hned v plnom (bielom) stave
   - mobilne menu je bez JS rozbalene v lisce, hamburger je skryty cez CSS
   - formulare a <details> su nativne
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var HAS_IO = 'IntersectionObserver' in window;

  function barHeight() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--fbl-bar-h');
    var n = parseInt(v, 10);
    return isNaN(n) ? 64 : n;
  }

  /* =====================================================================
     1. <fbl-reveal> — jedina animacia na stranke: jemne odkrytie obsahu
     ===================================================================== */

  /* Prvky, ktore vojdu do obrazovky naraz (napr. rad kariet), dostanu
     postupne oneskorenie zhora nadol a zlava doprava — naskakuju jeden po
     druhom. Odkrytie sa spusti, az ked je prvok kusok v obrazovke, aby bolo
     pohyb naozaj vidno a neprebehol este pod okrajom. */
  var revealIO = null;
  function revealObserver() {
    if (revealIO || !HAS_IO) return revealIO;
    revealIO = new IntersectionObserver(function (entries, obs) {
      /* Poradie sa pocita v ramci kazdej sekcie zvlast — nadpis, potom text,
         potom karty zlava doprava, rad po rade. */
      var groups = new Map();
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var host = e.target.closest('fbl-reveal') || document.body;
        if (!groups.has(host)) groups.set(host, []);
        groups.get(host).push(e);
      });
      groups.forEach(function (list) {
        list
          .sort(function (a, b) {
            var dy = a.boundingClientRect.top - b.boundingClientRect.top;
            return Math.abs(dy) > 8 ? dy : a.boundingClientRect.left - b.boundingClientRect.left;
          })
          .forEach(function (e, i) {
            e.target.style.setProperty('--fbl-delay', Math.min(i, 10) * 80 + 'ms');
            e.target.classList.add('is-in');
            obs.unobserve(e.target);
          });
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });
    return revealIO;
  }

  class FblReveal extends HTMLElement {
    connectedCallback() {
      var items = this.querySelectorAll('.fbl-reveal:not(.is-in)');
      if (REDUCED.matches || !HAS_IO) {
        items.forEach(function (el) { el.classList.add('is-in'); });
        return;
      }
      var obs = revealObserver();
      items.forEach(function (el) { obs.observe(el); });
    }
  }

  /* =====================================================================
     2. <fbl-header> — stav hlavicky, scroll-spy a mobilne menu
     ===================================================================== */

  /* Priehladna hlavicka sa zapina len vtedy, ked je hero prvou sekciou
     stranky. Rovnaky selektor pouziva CSS v fbl-header.liquid. */
  var HERO_SENTINEL = 'main > .shopify-section:first-child [data-fbl-hero-sentinel]';

  class FblHeader extends HTMLElement {
    connectedCallback() {
      this.visible = new Set();
      this.observers = [];

      /* Theme Editor prekresluje sekcie. Ked sa vymeni hero, stary sentinel
         zmizne z DOM a observer by sledoval prazdne miesto — preto sa po
         kazdom prekresleni nastavime nanovo. */
      this.refresh = this.refresh.bind(this);
      document.addEventListener('shopify:section:load', this.refresh);
      document.addEventListener('shopify:section:unload', this.refresh);

      this.init();
      this.initMenu();
      this.initLang();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.refresh);
      document.removeEventListener('shopify:section:unload', this.refresh);
      window.cancelAnimationFrame(this.raf);
      this.teardown();
      if (this.mq && this.onMq) this.mq.removeEventListener('change', this.onMq);
      if (this.onLangDocClick) document.removeEventListener('click', this.onLangDocClick);
      this.unlockScroll();
    }

    init() {
      this.initState();
      this.initSpy();
    }

    teardown() {
      this.observers.forEach(function (o) { o.disconnect(); });
      this.observers = [];
      this.visible.clear();
    }

    refresh() {
      window.cancelAnimationFrame(this.raf);
      this.raf = window.requestAnimationFrame(function () {
        this.teardown();
        this.init();
      }.bind(this));
    }

    /* --- Priehladna nad hero, plna po odscrollovani hero ---------------
       Sentinel v hero konci presne o vysku listy vyssie, nez je spodok
       hero. Ked prestane pretinat viewport, fotka je za listou a hlavicka
       sa prepne do plneho stavu. Ziadny scroll listener. */
    initState() {
      var sentinel = document.querySelector(HERO_SENTINEL);
      var canBeOver = this.dataset.transparent === 'true';

      if (!sentinel || !canBeOver || !HAS_IO) {
        this.setState(true);
        return;
      }
      this.setState(false);
      var io = new IntersectionObserver(function (entries) {
        if (!sentinel.isConnected) return this.refresh();
        this.setState(!entries[entries.length - 1].isIntersecting);
      }.bind(this), { threshold: 0 });
      io.observe(sentinel);
      this.observers.push(io);
    }

    setState(solid) {
      this.dataset.state = solid ? 'solid' : 'over';
    }

    /* --- Scroll-spy pre kotvy v ramci tej istej stranky ----------------
       Odkazy na ine stranky riesi Liquid cez aria-current="page".
       Tu sa prepina len aria-current="true" pri kotvach. */
    initSpy() {
      if (!HAS_IO) return;
      var map = new Map();
      this.querySelectorAll('a[href*="#"]').forEach(function (a) {
        if (!a.hash || a.hash === '#') return;
        if (a.pathname && a.pathname !== window.location.pathname) return;
        var target = null;
        try { target = document.getElementById(decodeURIComponent(a.hash.slice(1))); } catch (e) { return; }
        if (!target) return;
        if (!map.has(target)) map.set(target, []);
        map.get(target).push(a);
      });
      if (!map.size) return;

      this.spy = map;
      var band = barHeight() + 8;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) this.visible.add(e.target);
          else this.visible.delete(e.target);
        }.bind(this));
        this.paintSpy();
      }.bind(this), { rootMargin: '-' + band + 'px 0px -55% 0px', threshold: 0 });

      map.forEach(function (links, target) { io.observe(target); });
      this.observers.push(io);
    }

    paintSpy() {
      /* Kazdy zaznam si overime este raz proti realnej polohe. IntersectionObserver
         vie poslat udaj, ktory medzitym prestal platit — potom by podciarknutie
         ostalo visiet. */
      var top = barHeight() + 8;
      var bottom = (window.innerHeight || document.documentElement.clientHeight) * 0.45;
      var best = null;
      this.visible.forEach(function (el) {
        if (!el.isConnected) return;
        var r = el.getBoundingClientRect();
        if (r.bottom < top || r.top > bottom) return;
        if (!best || r.top < best.getBoundingClientRect().top) best = el;
      });
      this.spy.forEach(function (links, target) {
        links.forEach(function (a) {
          if (a.getAttribute('aria-current') === 'page') return;
          if (target === best) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      });
    }

    /* --- Mobilne off-canvas menu v <dialog> ---------------------------
       showModal() dava focus trap, Esc a inert pozadia nativne.
       Dozamkneme len scroll pozadia a pridame animaciu. */
    initMenu() {
      this.toggle = this.querySelector('[data-fbl-menu-toggle]');
      this.dialog = this.querySelector('[data-fbl-menu]');
      if (!this.toggle || !this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);

      this.toggle.addEventListener('click', this.open);
      this.querySelectorAll('[data-fbl-menu-close]').forEach(function (b) {
        b.addEventListener('click', this.close);
      }.bind(this));

      this.dialog.addEventListener('cancel', function (e) { e.preventDefault(); this.close(); }.bind(this));
      this.dialog.addEventListener('click', function (e) {
        if (e.target === this.dialog || e.target.closest('a')) this.close();
      }.bind(this));
      this.dialog.addEventListener('close', function () {
        this.toggle.setAttribute('aria-expanded', 'false');
        this.unlockScroll();
        this.toggle.focus({ preventScroll: true });
      }.bind(this));

      this.mq = window.matchMedia('(min-width: 990px)');
      this.onMq = function (e) { if (e.matches && this.dialog.open) this.dialog.close(); }.bind(this);
      this.mq.addEventListener('change', this.onMq);
    }

    open() {
      this.dialog.showModal();
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('fbl-lock');
      window.requestAnimationFrame(function () { this.dialog.dataset.open = 'true'; }.bind(this));
    }

    close() {
      if (!this.dialog.open) return;
      delete this.dialog.dataset.open;
      var done = function () { if (this.dialog.open) this.dialog.close(); }.bind(this);
      if (REDUCED.matches) return done();
      window.setTimeout(done, 240);
    }

    unlockScroll() { document.documentElement.classList.remove('fbl-lock'); }

    /* --- Prepinac jazyka v liste --------------------------------------
       Zoznam je obycajny submit formular temy Shopify, tu sa rieši len
       rozbalenie. Riadok v mobilnom menu ziadny JS nepotrebuje. */
    initLang() {
      try {
        this.langBox = this.querySelector('[data-fbl-lang]');
        this.langToggle = this.querySelector('[data-fbl-lang-toggle]');
        if (!this.langBox || !this.langToggle) return;

        this.closeLang = this.closeLang.bind(this);
        this.onLangDocClick = function (e) {
          if (!this.langBox.contains(e.target)) this.closeLang();
        }.bind(this);

        this.langToggle.addEventListener('click', function () {
          if (this.langBox.dataset.open === 'true') this.closeLang();
          else this.openLang();
        }.bind(this));

        this.langBox.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape' && e.key !== 'Esc') return;
          if (this.langBox.dataset.open !== 'true') return;
          e.stopPropagation();
          this.closeLang();
          this.langToggle.focus({ preventScroll: true });
        }.bind(this));

        /* Odchod fokusu (Tab za posledny jazyk) zoznam zatvori. */
        this.langBox.addEventListener('focusout', function (e) {
          if (!this.langBox.contains(e.relatedTarget)) this.closeLang();
        }.bind(this));

        document.addEventListener('click', this.onLangDocClick);
      } catch (err) {
        if (window.console && console.warn) console.warn('[fbl] prepínač jazyka:', err);
      }
    }

    openLang() {
      this.langBox.dataset.open = 'true';
      this.langToggle.setAttribute('aria-expanded', 'true');
    }

    closeLang() {
      if (!this.langBox || this.langBox.dataset.open !== 'true') return;
      delete this.langBox.dataset.open;
      this.langToggle.setAttribute('aria-expanded', 'false');
    }
  }

  /* =====================================================================
     3. <fbl-announce> — rotovanie sprav v oznamovacej liste
     Rotuje len pri dvoch a viac spravach. Pri prefers-reduced-motion
     a bez JS zostane viditelna prva sprava.
     ===================================================================== */

  class FblAnnounce extends HTMLElement {
    connectedCallback() {
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-fbl-msg]'));
      if (this.items.length < 2 || REDUCED.matches) return;

      this.i = 0;
      this.delay = Math.max(3000, parseInt(this.dataset.interval, 10) * 1000 || 5000);
      this.tick = this.tick.bind(this);
      this.stop = this.stop.bind(this);
      this.start = this.start.bind(this);

      this.addEventListener('mouseenter', this.stop);
      this.addEventListener('mouseleave', this.start);
      this.addEventListener('focusin', this.stop);
      this.addEventListener('focusout', this.start);
      this.start();
    }

    disconnectedCallback() { this.stop(); window.clearTimeout(this.swap); }

    start() { this.stop(); this.timer = window.setInterval(this.tick, this.delay); }
    stop() { if (this.timer) window.clearInterval(this.timer); this.timer = null; }

    /* Najprv zmizne stara sprava, az potom nabehne nova. Keby sa prelinali
       naraz, na chvilu by boli cez seba dva texty. */
    tick() {
      var prev = this.items[this.i];
      this.i = (this.i + 1) % this.items.length;
      var next = this.items[this.i];
      prev.dataset.active = 'false';
      window.clearTimeout(this.swap);
      this.swap = window.setTimeout(function () { next.dataset.active = 'true'; }, 260);
    }
  }

  /* =====================================================================
     5. <fbl-buy> — nakupny blok produktovej stranky
     Bez JS funguje vsetko okrem lepkavej listy: galeria je scroll-snap pas,
     miniatury su kotvy a varianty su radio inputy vo formulari. Tu to len
     vylepsujeme — prepis ceny, aktivna miniatura, zvacsenie a lista.
     ===================================================================== */

  var CLOSE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* Zvacsenie fotky v <dialog>. Pouziva ho nakupny blok aj fotka etikety
     v zlozeni. Bez podpory <dialog> sa odkaz necha tak a fotka sa otvori
     ako subor — preto je zvacsenie vzdy obycajny <a href>. */
  function openZoom(host, src, alt) {
    var dlg = host.fblDialog;
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.className = 'fbl__zoom-dlg';
      dlg.innerHTML = '<button type="button" class="fbl__zoom-dlg-close" aria-label="Zavrieť">' + CLOSE_SVG + '</button><img alt="">';
      dlg.addEventListener('click', function (ev) {
        if (ev.target === dlg || (ev.target.closest && ev.target.closest('.fbl__zoom-dlg-close'))) dlg.close();
      });
      host.appendChild(dlg);
      host.fblDialog = dlg;
    }
    var img = dlg.querySelector('img');
    img.src = src;
    img.alt = alt || '';
    if (typeof dlg.showModal === 'function') dlg.showModal();
  }

  class FblBuy extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-fbl-rail]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-fbl-thumb]'));
      this.bar = this.querySelector('[data-fbl-bar]');
      this.addBtn = this.querySelector('[data-fbl-add]');
      this.qty = this.querySelector('[data-fbl-qty-input]');

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);

      this.syncQty();
      this.watchSlides();
      this.watchBar();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      if (this.slideIO) this.slideIO.disconnect();
      if (this.barIO) this.barIO.disconnect();
    }

    /* --- Počet kusov ----------------------------------------------------
       Pole je sucastou formulara, takze pocet posle aj lepkava lista na
       mobile — jej tlacidlo odosiela ten isty formular cez atribut form. */

    step(by) {
      if (!this.qty) return;
      var n = parseInt(this.qty.value, 10);
      if (isNaN(n)) n = 1;
      this.qty.value = Math.max(1, n + by);
      this.syncQty();
    }

    /* Pocet sa da aj napisat rukou */
    onInput(e) {
      if (e.target.closest && e.target.closest('[data-fbl-qty-input]')) this.syncQty();
    }

    /* Pri jednom kuse nema "−" co odobrat */
    syncQty() {
      if (!this.qty) return;
      var n = parseInt(this.qty.value, 10);
      var down = this.querySelector('[data-fbl-qty="-1"]');
      if (down) down.disabled = !(n > 1);
    }

    /* --- Galéria -------------------------------------------------------- */

    onClick(e) {
      if (!e.target.closest) return;

      var qtyBtn = e.target.closest('[data-fbl-qty]');
      if (qtyBtn) {
        this.step(parseInt(qtyBtn.dataset.fblQty, 10) || 0);
        return;
      }

      var thumb = e.target.closest('[data-fbl-thumb]');
      if (thumb && this.rail) {
        var slide = document.getElementById((thumb.getAttribute('href') || '').slice(1));
        if (slide) {
          e.preventDefault();
          slide.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
          this.setActive(this.thumbs.indexOf(thumb));
        }
        return;
      }

      var zoom = e.target.closest('[data-fbl-zoom]');
      /* Bez podpory <dialog> necháme odkaz tak — otvorí fotku v novom okne. */
      if (zoom && typeof HTMLDialogElement === 'function') {
        e.preventDefault();
        var img = zoom.querySelector('img');
        openZoom(this, zoom.getAttribute('href'), img ? img.alt : '');
      }
    }

    setActive(i) {
      this.thumbs.forEach(function (t, n) {
        if (n === i) t.dataset.active = 'true';
        else t.removeAttribute('data-active');
      });
    }

    watchSlides() {
      if (!this.rail || !HAS_IO || !this.thumbs.length) return;
      var self = this;
      var slides = Array.prototype.slice.call(this.rail.children);
      this.slideIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) self.setActive(slides.indexOf(en.target));
        });
      }, { root: this.rail, threshold: 0.6 });
      slides.forEach(function (sl) { self.slideIO.observe(sl); });
    }

    /* --- Lepkavá lišta na mobile ---------------------------------------- */

    /* Lišta vyjde, až keď nákupné tlačidlo odíde HORE z obrazovky. Kým je
       pod ňou (používateľ ešte neprešiel okolo), nemá sa prečo ukazovať. */
    watchBar() {
      if (!this.bar || !this.addBtn || !HAS_IO) return;
      var bar = this.bar;
      this.barIO = new IntersectionObserver(function (entries) {
        var en = entries[0];
        var past = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.dataset.show = past ? 'true' : 'false';
      }, { threshold: 0 });
      this.barIO.observe(this.addBtn);
    }
  }

  /* =====================================================================
     6. <fbl-zoom> — klik na fotku ju zvacsi (fotka etikety v zlozeni)
     ===================================================================== */

  class FblZoom extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }
    disconnectedCallback() { this.removeEventListener('click', this.onClick); }
    onClick(e) {
      if (!e.target.closest || typeof HTMLDialogElement !== 'function') return;
      var link = e.target.closest('[data-fbl-zoom]');
      if (!link) return;
      e.preventDefault();
      var img = link.querySelector('img');
      openZoom(this, link.getAttribute('href'), img ? img.alt : '');
    }
  }

  /* =====================================================================
     7. Stav tlacidla pocas pridavania do kosika

     Pridanie samotne rieši <product-form-component> z temy Horizon. My
     len prepiname stav tlacidla, aby sa nedalo kliknut dvakrat.

     Nazvy udalosti NIE SU vymyslene — su to konstanty z @shopify/events,
     ktore tema nacitava z https://cdn.shopify.com/storefront/standard-events.js
     a na ktore pocuva aj jej vlastny <cart-drawer-component>:
       shopify:cart:lines-update  (StandardEvents.cartLinesUpdate)
       shopify:cart:error         (StandardEvents.cartError)
     Udalost bublá a data su priamo na nej (event.action, event.promise),
     nie v event.detail.
     ===================================================================== */

  var CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var CART_ERROR = 'shopify:cart:error';
  var BUSY_TIMEOUT = 8000;

  var busyButtons = [];
  var busyTimer = null;

  /* Tlacidla formulara vratane tych mimo neho (lepkava lista ma atribut
     form="…", takze odosiela ten isty formular) */
  function formButtons(form) {
    var list = Array.prototype.slice.call(form.querySelectorAll('button[type="submit"]'));
    /* Pozor: form.id vrati INPUT s name="id", nie atribut id — formular
       sprístupňuje svoje polia ako vlastnosti. Preto getAttribute. */
    var id = form.getAttribute('id');
    if (id) {
      Array.prototype.push.apply(
        list,
        Array.prototype.slice.call(document.querySelectorAll('button[form="' + id + '"]'))
      );
    }
    return list;
  }

  function busyOff() {
    window.clearTimeout(busyTimer);
    busyTimer = null;
    busyButtons.forEach(function (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    });
    busyButtons = [];
  }

  function busyOn(form) {
    busyOff();
    busyButtons = formButtons(form);
    busyButtons.forEach(function (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    });
    /* Poistka: keby tema udalost neposlala, tlacidlo nesmie ostat mrtve */
    busyTimer = window.setTimeout(busyOff, BUSY_TIMEOUT);
  }

  /* Kazdy posluchac je v try/catch. Toto je iba kozmetika tlacidla —
     keby tu nieco zlyhalo, nesmie to zhodit stranku, nakup ani editor temy.
     Samotne pridanie do kosika na tomto kode nijako nezavisi. */
  function guard(fn) {
    return function (e) {
      try {
        fn(e);
      } catch (err) {
        if (window.console && console.warn) console.warn('[fbl] stav tlačidla:', err);
      }
    };
  }

  document.addEventListener(
    'submit',
    guard(function (e) {
      var form = e.target;
      if (!form || form.nodeName !== 'FORM' || !form.closest) return;
      if (!form.closest('product-form-component')) return;
      busyOn(form);
    })
  );

  document.addEventListener(
    CART_LINES_UPDATE,
    guard(function (e) {
      var p = e.promise || (e.detail && e.detail.promise);
      if (p && typeof p.then === 'function') p.then(busyOff, busyOff);
      else busyOff();
    })
  );

  document.addEventListener(CART_ERROR, guard(busyOff));

  /* =====================================================================
     8. <fbl-countdown> — odpocet na password stranke

     Cielovy cas pride uz ako plne ISO s posunom obchodu (napr.
     2026-10-20T18:00:00+02:00), takze sa vyhodnoti rovnako bez ohladu
     na to, v akom pasme je navstevnik.

     Cisla su vyrenderovane uz v Liquide, takze bez JS je na stranke
     spravny (len nehybuci sa) odpocet. Tento kod ich len obnovuje.
     ===================================================================== */

  class FblCountdown extends HTMLElement {
    connectedCallback() {
      try {
        var until = this.getAttribute('data-until');
        this.target = until ? new Date(until).getTime() : NaN;
        if (isNaN(this.target)) return;

        this.cells = {
          days: this.querySelector('[data-cd="days"]'),
          hours: this.querySelector('[data-cd="hours"]'),
          minutes: this.querySelector('[data-cd="minutes"]'),
          seconds: this.querySelector('[data-cd="seconds"]')
        };
        this.grid = this.querySelector('[data-fbl-cd-grid]');
        this.done = this.querySelector('[data-fbl-cd-done]');
        this.date = this.querySelector('[data-fbl-cd-date]');

        this.tick = this.tick.bind(this);
        this.start = this.start.bind(this);
        this.stop = this.stop.bind(this);
        this.onVisibility = this.onVisibility.bind(this);

        this.start();
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('pagehide', this.stop);
      } catch (err) {
        if (window.console && console.warn) console.warn('[fbl] odpočet:', err);
      }
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pagehide', this.stop);
    }

    /* Cas sa pri kazdom tiku pocita z Date.now(), takze po vrateni do zalozky
       alebo po uspani casovaca naskoci spravna hodnota, nie dopocitana. */
    start() {
      this.stop();
      this.tick();
      if (!this.finished) this.timer = window.setInterval(this.tick, 1000);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.start();
    }

    tick() {
      var left = Math.floor((this.target - Date.now()) / 1000);

      if (left <= 0) {
        this.finished = true;
        this.stop();
        if (this.grid) this.grid.hidden = true;
        if (this.date) this.date.hidden = true;
        if (this.done) this.done.hidden = false;
        return;
      }

      var pad = function (n) { return n < 10 ? '0' + n : String(n); };
      this.set('days', pad(Math.floor(left / 86400)));
      this.set('hours', pad(Math.floor((left % 86400) / 3600)));
      this.set('minutes', pad(Math.floor((left % 3600) / 60)));
      this.set('seconds', pad(left % 60));
    }

    set(key, value) {
      var el = this.cells[key];
      if (el && el.textContent !== String(value)) el.textContent = value;
    }
  }

  /* --------------------------------------------------------------------- */
  function define(name, cls) { if (!customElements.get(name)) customElements.define(name, cls); }
  define('fbl-reveal', FblReveal);
  define('fbl-header', FblHeader);
  define('fbl-announce', FblAnnounce);
  define('fbl-buy', FblBuy);
  define('fbl-zoom', FblZoom);
  define('fbl-countdown', FblCountdown);
})();